import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { BASEMAP, basemapUrlFor, prefersDark, onColorSchemeChange } from '../utils/basemap';
import './Map.css'; // Import the new CSS file
import L from 'leaflet';
import emitter from '../utils/emitter';
import MySpotModal from './MySpotModal';
import RequesterSideDrawer from './RequesterSideDrawer';
import DeleteConfirmationModal from './DeleteConfirmationModal'; // Import the new modal
import MessagesSideDrawer from './MessagesSideDrawer';
import carMarker from '../assets/images/car-marker.png';
import carMarker2x from '../assets/images/car-marker@2x.png';
import markerGreen from './icons/marker-icon-green.png';
import markerGreen2x from './icons/marker-icon-green-2x.png';
import markerRed from './icons/marker-icon-red.png';
import markerRed2x from './icons/marker-icon-red-2x.png';

// Fix for default marker icon not showing
delete L.Icon.Default.prototype._getIconUrl;

// Where YOU are.
//
// 2026-10-17: was a green teardrop — the same shape, in one of the same three traffic-light
// colours, as the pins that mean "a parking space". Reported by the person who knows this map best,
// who looked at it and read it as his own car. If he can misread it, everybody can: a teardrop on a
// map means "a thing at a place", and every other teardrop here is a space you might take.
//
// A blue dot instead, which is what every map on every platform uses for the viewer, and what the
// phone gets for free from MKUserLocation. It cannot be confused with a destination because it is
// not shaped like one. #0A84FF is iOS's own system blue, already used by this screen's locate-me
// button, so the two now agree about what blue means here.
const userIcon = L.divIcon({
  className: 'user-location-div-icon',
  html: '<div class="user-location-dot"></div>',
  // 2026-10-17: 22 -> 20, about 10% down. It reads as "you" at a glance either way, and the
  // smaller dot sits less heavily on a street it is not describing.
  iconSize: [20, 20],
  iconAnchor: [10, 10],   // centred ON the point, unlike a pin, which hangs above it
  popupAnchor: [0, -11],
});

// Function to create a custom DivIcon with a red dot
const createCustomIcon = (iconUrl, iconRetinaUrl, hasNewRequest, filter) => {
  const iconHtml = `
    <div style="position: relative; width: 25px; height: 41px;">
      <img src="${iconUrl}" srcset="${iconRetinaUrl} 2x" alt="Marker" style="width: 100%; height: 100%;${filter ? ` filter: ${filter};` : ''}"/>
      ${hasNewRequest ? '<div class="new-request-dot"></div>' : ''}
    </div>
  `;
  return L.divIcon({
    className: 'custom-div-icon',
    html: iconHtml,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
  });
};

// Traffic-light urgency scale, shared with the mobile client (2026-08-15): red = "spot free, act
// fast", orange = "soon to leave", green = "you have time to get there". Same 3 hex values used on
// mobile (ParksphereMobileApp/components/Map.js).
const COLOR_RED = '#E53935';
const COLOR_ORANGE = '#FB8C00';
const COLOR_GREEN = '#2E7D32';

// The owner's OWN car, and a space already granted to them — the mobile app's badge, brought over
// 2026-10-17 so the two clients show the same thing in the same place.
//
// Deliberately not the traffic-light teardrop every other spot gets. That scale answers "how soon
// could I have this?", a question your own car does not pose: it is the one marker on the map you
// cannot request, and showing it as a red pin put it in the same visual language as a space going
// begging. Mobile has drawn it as a distinct purple badge since 2026-10-05 and the web had not
// caught up.
//
// Geometry copied from ParksphereMobileApp/components/Map.js's styles so the two actually match:
// a 26px car inside 11.5px of padding and a 2px border is a 53px circle, with a 13px square
// rotated 45 degrees for the tail, overlapped by 7.6px so the join stays closed.
const COLOR_OWN_SPOT = '#673dcb';   // utils/spotColors.js's COLOR_OWN_SPOT
const BADGE_BORDER = '#D1C4E9';     // soft mauve, a little brand colour in the badge itself

const createOwnSpotIcon = (isAccepted, hasNewRequest) => {
  const fill = isAccepted ? COLOR_GREEN : COLOR_OWN_SPOT;
  // A granted space is somebody else's car park, not yours, so it keeps the badge but says P —
  // mirrors mobile's MaterialCommunityIcons "parking" glyph without depending on an icon font
  // being loaded before the map paints.
  const inner = isAccepted
    ? '<span style="font:700 22px/26px var(--font-body),sans-serif;color:#fff;display:block;width:26px;text-align:center">P</span>'
    : `<img src="${carMarker}" srcset="${carMarker2x} 2x" alt="" style="width:26px;height:26px;display:block"/>`;
  return L.divIcon({
    className: 'own-spot-div-icon',
    html: `
      <div class="own-spot-marker">
        <div class="own-spot-bubble" style="background:${fill};border-color:${BADGE_BORDER}">${inner}</div>
        <div class="own-spot-tail" style="background:${fill};border-color:${BADGE_BORDER}"></div>
        ${hasNewRequest ? '<div class="new-request-dot"></div>' : ''}
      </div>`,
    // 53 wide; 53 of bubble + 13 of tail less the 7.6 overlap is 58.4 tall. Anchored at the point
    // of the tail, which is where the car actually is.
    iconSize: [53, 58],
    iconAnchor: [26.5, 58],
    popupAnchor: [0, -52],
  });
};

// Auto-detected: color reflects the native return-detector's CONFIDENCE zone (SOFT/COMMIT/VACATING),
// a discrete signal — not a continuous time countdown. An 8-minute COMMIT-stage ETA should still read
// as more urgent than a 6-minute SOFT hint, so time-since-declared alone would misrepresent it.
// Manual declare: no confidence concept, just a literal countdown to the stated departure time —
// recomputed against `now` so a spot visibly ticks green -> orange -> red as time passes.
const getSpotColor = (spot, now) => {
  if (spot.is_auto_detected) {
    switch (spot.status) {
      case 'soon_free': return COLOR_GREEN;   // SOFT — early signal, plenty of time
      case 'committed': return COLOR_ORANGE;  // COMMIT — confirmed, ETA known
      case 'vacating': return COLOR_RED;      // driving away now — act fast
      case 'free': return COLOR_GREEN;        // already available
      case 'occupied':
      default: return COLOR_RED;              // owner-only view; not otherwise visible
    }
  }
  if (spot.time_to_leave == null) return COLOR_GREEN; // shouldn't happen for a manual declare, safe default
  const declaredMs = new Date(spot.declared_at).getTime();
  const remainingMin = (spot.time_to_leave * 60000 - (now - declaredMs)) / 60000;
  if (remainingMin <= 2) return COLOR_RED;
  if (remainingMin <= 5) return COLOR_ORANGE;
  return COLOR_GREEN;
};

// Only green/red pin PNGs exist as assets — orange is approximated by hue-rotating the red pin
// rather than adding a third image file.
const getMarkerIconAssets = (color) => {
  if (color === COLOR_GREEN) return { url: markerGreen, url2x: markerGreen2x, filter: null };
  if (color === COLOR_ORANGE) return { url: markerRed, url2x: markerRed2x, filter: 'hue-rotate(25deg) saturate(1.4)' };
  return { url: markerRed, url2x: markerRed2x, filter: null };
};

const Map = ({ parkingSpots, userLocation: appUserLocation, currentUserId, acceptedSpot, requesterEta, requesterArrived, onAcknowledgeArrival, onSpotDeleted, addNotification: appAddNotification, onRequestStatusChange, currentUsername, pendingRequests, spotRequests, onOpenChat, unreadMessages, onRateRequester, onOpenRequesterDetails, isMessagesDrawerOpen, setIsMessagesDrawerOpen, serverUrl, expiredSpotIds, spotToOpenDrawer, arrivalRejectedSpotId }) => {
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  
  const [eta, setEta] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [popup, setPopup] = useState(null);
  const [drawerSpot, setDrawerSpot] = useState(null);
  const [requesterDrawerSpot, setRequesterDrawerSpot] = useState(null);
  const [ownerCarDetails, setOwnerCarDetails] = useState(null);
  const [myAddress, setMyAddress] = useState(null);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false); // New state for delete modal
  const [spotToDeleteId, setSpotToDeleteId] = useState(null); // New state to store spot ID to delete
  const [newRequestSpotIds, setNewRequestSpotIds] = useState([]); // New state for spots with new requests

  // Which basemap to draw. Follows the system, and keeps following it: without the subscription
  // the tiles stay light until the next reload while every other surface has already turned over.
  const [isDark, setIsDark] = useState(prefersDark);
  useEffect(() => onColorSchemeChange(setIsDark), []);

  useEffect(() => {
    console.log('Map.js: spotRequests prop updated:', JSON.stringify(spotRequests, null, 2)); // DEBUG LOG
  }, [spotRequests]);

  useEffect(() => {
    if (spotToOpenDrawer) {
      console.log('Map.js: Opening drawer for spotToOpenDrawer:', spotToOpenDrawer);
      handleOwnerSpotClick(spotToOpenDrawer);
    }
  }, [spotToOpenDrawer]);

  useEffect(() => {
    if (acceptedSpot) {
      setRequesterDrawerSpot(acceptedSpot);
      const fetchOwnerDetails = async () => {
        const token = localStorage.getItem('token');
        try {
          const response = await fetch(`/api/users/${acceptedSpot.user_id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (response.ok) {
            const data = await response.json();
            setOwnerCarDetails(data);
          } else {
            console.error('Error fetching owner car details:', response.statusText);
          }
        } catch (error) {
          console.error('Error fetching owner car details:', error);
        }
      };
      fetchOwnerDetails();
    }
  }, [acceptedSpot]);

  useEffect(() => {
    const handleNewRequest = (data) => {
      setNewRequestSpotIds(prev => [...prev, data.spotId]);
      if (drawerSpot && drawerSpot.id === data.spotId) {
        // If the drawer is open for this spot, refresh its requests
        handleOwnerSpotClick(drawerSpot);
      }
    };

    emitter.on('spotRequest', handleNewRequest);

    return () => {
      emitter.off('spotRequest', handleNewRequest);
    };
  }, [drawerSpot]);

  useEffect(() => {
    const handleNewRequest = () => {
      if (drawerSpot) {
        handleOwnerSpotClick(drawerSpot);
      }
    };

    emitter.on('new-request', handleNewRequest);

    return () => {
      emitter.off('new-request', handleNewRequest);
    };
  }, [drawerSpot]);

  useEffect(() => {
    if (requesterDrawerSpot) {
      const spotExists = parkingSpots.some(spot => spot.id === requesterDrawerSpot.id);
      if (!spotExists) {
        setRequesterDrawerSpot(null);
      }
    }
    if (drawerSpot) {
      const spotExists = parkingSpots.some(spot => spot.id === drawerSpot.id);
      if (!spotExists) {
        setDrawerSpot(null);
      }
    }
  }, [parkingSpots, requesterDrawerSpot, drawerSpot]);

  useEffect(() => {
    const handleCloseDrawer = () => {
      setDrawerSpot(null);
    };

    emitter.on('closeOwnerDrawer', handleCloseDrawer);

    return () => {
      emitter.off('closeOwnerDrawer', handleCloseDrawer);
    };
  }, []);

  // Effect to close side drawers if the spot expires
  useEffect(() => {
    if (drawerSpot && expiredSpotIds.includes(drawerSpot.id)) {
      setDrawerSpot(null);
    }
    if (requesterDrawerSpot && expiredSpotIds.includes(requesterDrawerSpot.id)) {
      setRequesterDrawerSpot(null);
    }
  }, [expiredSpotIds, drawerSpot, requesterDrawerSpot]);

  // 2026-10-17: handleNewButtonClick stood here and opened EditSpotModal from the owner's drawer.
  // Editing a spot means setting a departure time, which is a promise somebody else collects by
  // driving to you — the same rule that took the declare off the web. The modal that replaced the
  // drawer does not offer it, so nothing reaches EditSpotModal from here any more.

  const handleDelete = useCallback((spotId) => {
    setSpotToDeleteId(spotId);
    setShowDeleteConfirmationModal(true);
  }, []);

  const confirmDeleteSpot = () => {
    onSpotDeleted(spotToDeleteId);
    setShowDeleteConfirmationModal(false);
    setSpotToDeleteId(null);
    setDrawerSpot(null); // Close the spot card after deletion
  };

  useEffect(() => {
    if (popup && popupRef.current) {
      popupRef.current.openPopup();
    }
  }, [popup]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000); // Update every second
    return () => clearInterval(timer);
  }, []);

  const handleLocateMe = () => {
    if (mapRef.current && appUserLocation) {
      mapRef.current.flyTo(appUserLocation, 15); // Adjust zoom level as needed
    }
  };

  const fetchUserAddress = async (lat, lng) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await response.json();
      return data.display_name;
    } catch (error) {
      console.error('Error fetching address:', error);
      return 'Could not fetch address.';
    }
  };

  // Looked up when the popup OPENS rather than on every render: nominatim is a courtesy service
  // with a usage policy, and this is a question the owner asks occasionally, not continuously.
  // Re-fetched each open, because the answer changes when they move.
  const lookUpMyAddress = async () => {
    setMyAddress(null);
    if (!appUserLocation) return;
    const address = await fetchUserAddress(appUserLocation[0], appUserLocation[1]);
    setMyAddress(address || 'Address unavailable');
  };

  useEffect(() => {
    if (acceptedSpot) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          // if (distance < 0.05) { // 50 meters
          //   setIsConfirming(true);
          //   if (window.confirm("It looks like you've arrived. Confirm your arrival?")) {
          //     handleConfirmArrival(acceptedSpot.id);
          //     navigator.geolocation.clearWatch(watchId);
          //   } else {
          //     setIsConfirming(false); // Allow the dialog to reappear if the user cancels
          //   }
          // }

          // Fetch ETA every 10 seconds
          const now = Date.now();
          if (!eta || (now - eta.lastUpdated) > 10000) {
            const fetchEta = async () => {
              const token = localStorage.getItem('token');
              const response = await fetch('/api/eta', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ requesterLat: latitude, requesterLon: longitude, spotId: acceptedSpot.id }),
              });
              const data = await response.json();
              setEta({ value: data.eta, lastUpdated: Date.now() });
            };
            fetchEta();
          }
        },
        (error) => {
          console.error("Error watching position:", error);
        },
        { enableHighAccuracy: true }
      );

      return () => {
        navigator.geolocation.clearWatch(watchId);
      };
    }
  }, [acceptedSpot, eta, appAddNotification]);

  // 2026-10-17: the pin-drop mode that stood here is gone with the declare it existed to place.
  // It let a browser put a space anywhere on the map — not where the device was — and a space is a
  // promise a stranger collects by driving. See App.js.

  // Helper function to format remaining time for display
  const formatRemainingTime = (declaredAt, timeToLeave) => {
    if (timeToLeave == null) return 'Not specified';
    const declaredTime = new Date(declaredAt).getTime();
    const expirationTime = declaredTime + (timeToLeave * 60 * 1000);
    const remainingMinutes = (expirationTime - currentTime) / (60 * 1000);

    if (remainingMinutes <= 0) {
      return 'Expired';
    } else if (remainingMinutes < 1) {
      return '< 1 minute';
    } else {
      return `${Math.floor(remainingMinutes)} minutes`;
    }
  };

  // Helper function to determine if a circle should animate
  const shouldAnimate = (declaredAt, timeToLeave) => {
    if (timeToLeave == null) return false;
    const declaredTime = new Date(declaredAt).getTime();
    const expirationTime = declaredTime + (timeToLeave * 60 * 1000);
    const remainingMinutes = (expirationTime - currentTime) / (60 * 1000);

    // Animate if red (<= 2 minutes) AND less than 1 minute remaining
    return (remainingMinutes >= 0 && remainingMinutes <= 2) && (remainingMinutes < 1);
  };

  // 2026-10-17: handleRequest and handleArrived stood here. Asking for a space commits you to
  // drive to it and makes its owner wait; "I've arrived" is a claim about where your body is.
  // Neither is something a browser on a desk can honestly say, and both cost somebody else time
  // when they are wrong. Cancelling stays — it releases an obligation rather than creating one.

  const handleCancelRequest = async (spotId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      appAddNotification("You must be logged in to cancel a request.", 'default');
      return;
    }

    try {
      const response = await fetch(`${serverUrl}/api/cancel-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ spotId }),
      });

      if (response.ok) {
        appAddNotification(`Request for spot #${spotId} cancelled successfully.`, 'default');
        await onRequestStatusChange(spotId, 'cancelled'); // Notify App.js to update pending requests
        emitter.emit('spot-request-updated', spotId);
        if (mapRef.current) {
          mapRef.current.closePopup();
        }
      } else {
        const errorData = await response.json();
        appAddNotification(`Failed to cancel request: ${errorData.message}`, 'default');
      }
    } catch (error) {
      console.error('Error cancelling request:', error);
      appAddNotification('An error occurred while cancelling the request.', 'default');
    }
  };

  const handleOwnerSpotClick = async (spot) => {
    // The "you are here" bubble is Leaflet's, so Leaflet closes it — opening the car card over a
    // popup that is still up would leave two answers on screen to two different questions.
    mapRef.current?.closePopup();
    // Just set the spot, let the useEffect handle populating requests
    setDrawerSpot(spot);
    setNewRequestSpotIds(prev => prev.filter(id => id !== spot.id)); // Clear red dot
  };

  // Effect to update drawerSpot with latest data from parkingSpots or spotRequests
  useEffect(() => {
    if (drawerSpot) { // Only update if a drawer is currently open
      const currentSpotId = drawerSpot.id;
      const updatedSpotData = parkingSpots.find(spot => spot.id === currentSpotId);
      if (updatedSpotData) {
        const relevantRequests = spotRequests.filter(req => req.spotId === currentSpotId);
        const newDrawerSpot = { ...updatedSpotData, requests: relevantRequests };
        // Only update if the content of the requests array has changed
        // Or if the spot data itself has changed (e.g., time_to_leave)
        if (JSON.stringify(drawerSpot.requests) !== JSON.stringify(relevantRequests) ||
            JSON.stringify(drawerSpot) !== JSON.stringify(newDrawerSpot)) { // Added check for overall spot data change
          setDrawerSpot(newDrawerSpot);
        }
      }
    }
  }, [parkingSpots, spotRequests, drawerSpot]); // Added drawerSpot to dependencies to ensure it re-runs when drawerSpot itself changes

  // Effect to update requesterDrawerSpot with latest data from parkingSpots
  useEffect(() => {
    if (requesterDrawerSpot) {
      const currentSpotId = requesterDrawerSpot.id;
      const updatedSpotData = parkingSpots.find(spot => spot.id === currentSpotId);
      if (updatedSpotData && JSON.stringify(requesterDrawerSpot) !== JSON.stringify(updatedSpotData)) {
        setRequesterDrawerSpot(updatedSpotData);
      }
    }
  }, [parkingSpots, requesterDrawerSpot]);

  if (!appUserLocation || isNaN(appUserLocation[0]) || isNaN(appUserLocation[1])) {
    return <div>Loading map or getting your location...</div>;
  }

  const isSpotExpired = (spot) => {
    if (spot.time_to_leave == null) return false; // "not specified" — never expires client-side
    // 2026-10-31: the time is indicative now — a spot with a request on it stays until its owner ends
    // it (server/utils/handoverRules.js), so never hide your own, or one you have asked for. The server
    // removes the rest when they really expire.
    if (spot.user_id === currentUserId || spot.my_request_status) return false;
    const declaredTime = new Date(spot.declared_at).getTime();
    const expirationTime = declaredTime + (spot.time_to_leave * 60 * 1000);
    return currentTime >= expirationTime;
  };

  return (
    <>
      <MapContainer ref={mapRef} center={appUserLocation} zoom={13} style={{ height: '100%', width: '100%' }}>
        {/* See utils/basemap.js: the OSM URL this replaces was against OSMF's tile usage policy,
            which forbids a distributed app using their servers and allows them to block it without
            notice. `key` forces a clean remount when the theme flips — react-leaflet will update a
            TileLayer's url in place, but the cached tiles of the other scheme flash through first. */}
        <TileLayer
          key={isDark ? 'dark' : 'light'}
          url={basemapUrlFor(isDark)}
          attribution={BASEMAP.attribution}
          subdomains={BASEMAP.subdomains}
          maxZoom={BASEMAP.maxZoom}
        />
        <button className="locate-me-button" onClick={handleLocateMe}>
          <svg className="locate-me-icon" viewBox="0 0 24 24" width="19" height="19" fill="#0A84FF">
            <path d="M12 2 L19 21 L12 17 L5 21 Z" />
          </svg>
        </button>
        {/* 2026-10-17: clicking the dot used to slide a whole side drawer across the map to show
            one line of text. A popup is the map's own idiom for "what is at this point", it is
            anchored to the thing it describes so it needs no title to say what it is about, and
            Leaflet owns its positioning and dismissal. The drawer is gone. */}
        <Marker
          position={appUserLocation}
          icon={userIcon}
          eventHandlers={{ popupopen: () => lookUpMyAddress() }}
        >
          <Popup>
            <div className="here-popup">
              <span className="here-popup-label">You are here</span>
              {myAddress === null
                ? <span className="here-popup-muted">Looking up the street…</span>
                : <span className="here-popup-address">{myAddress}</span>}
            </div>
          </Popup>
        </Marker>

        {parkingSpots.filter(spot => !isSpotExpired(spot)).map(spot => {
          const lat = spot.lat;
          const lng = spot.lng;

          if (isNaN(lat) || isNaN(lng)) {
            console.warn(`Skipping invalid parking spot coordinates for ID: ${spot.id}. Lat: ${lat}, Lng: ${lng}`);
            return null;
          }

          const isOwner = spot.user_id === currentUserId;
          // A space granted to you is shown with the same badge as your own car — both are "yours"
          // in the only sense the map cares about: neither can be requested.
          const isAcceptedSpot = !!acceptedSpot && acceptedSpot.id === spot.id;
          const isOwnOrAccepted = isOwner || isAcceptedSpot;
          const isExactLocation = spot.isExactLocation; // Use the flag from the backend

          const spotColor = getSpotColor(spot, currentTime);
          const { url: markerIconUrl, url2x: markerIcon2xUrl, filter: markerFilter } = getMarkerIconAssets(spotColor);

          return (
            <React.Fragment key={spot.id}>
              {isExactLocation ? (
                <Marker
                  position={[lat, lng]}
                  icon={isOwnOrAccepted
                    ? createOwnSpotIcon(isAcceptedSpot, isOwner && newRequestSpotIds.includes(spot.id))
                    : createCustomIcon(markerIconUrl, markerIcon2xUrl, false, markerFilter)}
                  eventHandlers={{
                    click: async () => {
                      if (isOwner) {
                        handleOwnerSpotClick(spot);
                      } else if (acceptedSpot && acceptedSpot.id === spot.id) {
                        setRequesterDrawerSpot(spot);
                        const token = localStorage.getItem('token');
                        try {
                          const response = await fetch(`/api/users/${spot.user_id}`, {
                            headers: {
                              'Authorization': `Bearer ${token}`,
                            },
                          });
                          if (response.ok) {
                            const data = await response.json();
                            setOwnerCarDetails(data);
                          } else {
                            console.error('Error fetching owner car details:', response.statusText);
                          }
                        } catch (error) {
                          console.error('Error fetching owner car details:', error);
                        }
                      } else {
                        setRequesterDrawerSpot(spot);
                        setPopup(null); // Close any existing popup
                      }
                    },
                  }}
                >
                </Marker>
              ) : (
                <Circle
                  center={[lat, lng]}
                  radius={200}
                  pathOptions={{
                    color: spotColor,
                    fillColor: spotColor,
                    fillOpacity: 0.2
                  }}
                  className={shouldAnimate(spot.declared_at, spot.time_to_leave) ? "pulse-opacity" : ""}
                  eventHandlers={{
                    click: () => {
                      if (isOwner) {
                        handleOwnerSpotClick(spot);
                      } else {
                        setRequesterDrawerSpot(spot);
                        setPopup(null); // Close any existing popup
                      }
                    }
                  }}
                >
                </Circle>
              )}
            </React.Fragment>
          );
        })}
      </MapContainer>

      <MessagesSideDrawer
        isOpen={isMessagesDrawerOpen}
        onClose={() => setIsMessagesDrawerOpen(false)}
        onConversationClick={(user) => {
          onOpenChat(user);
          setIsMessagesDrawerOpen(false);
        }}
      />

      {/* 2026-10-17: your OWN car opens a modal, not this drawer — the phone shows a card and so
          does this now. The drawer listed Spot ID / Time to Expire / Spot Type / Comments, four
          fields about a LISTING; the modal is about a car. Requests for the space are not lost
          with it: they live in the Requests panel, which can decline them too. */}
      <MySpotModal
        spot={drawerSpot}
        onClose={() => setDrawerSpot(null)}
        onDelete={handleDelete}
        addNotification={appAddNotification}
      />
      <RequesterSideDrawer
        spot={requesterDrawerSpot}
        formatRemainingTime={formatRemainingTime}
        onCancelRequest={handleCancelRequest}
        hasPendingRequest={requesterDrawerSpot && pendingRequests.includes(requesterDrawerSpot.id)}
        isAcceptedSpot={acceptedSpot && requesterDrawerSpot && acceptedSpot.id === requesterDrawerSpot.id}
        ownerCarDetails={ownerCarDetails}
        onClose={() => {
          setRequesterDrawerSpot(null);
          setOwnerCarDetails(null);
        }}
        onRejected={(spotId) => onRequestStatusChange(spotId, 'cancelled')}
        onOpenChat={onOpenChat}
        unreadMessages={unreadMessages}
        userLocation={appUserLocation}
        addNotification={appAddNotification}
        currentTime={currentTime}
        arrivalRejectedSpotId={arrivalRejectedSpotId}
      />

      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          isOpen={showDeleteConfirmationModal}
          onClose={() => setShowDeleteConfirmationModal(false)}
          onConfirm={confirmDeleteSpot}
          message={`Are you sure you want to delete parking spot #${spotToDeleteId}? This action cannot be undone.`}
        />
      )}
    </>
  );
};

export default Map;
